// custom-print-preview.tsx
import moment from "moment";
import React, { useState, useRef } from "react";
import { createRoot } from "react-dom/client";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import { QRCodeComponent } from "./QrCode";
type PaymentState = "not_paid" | "partial" | "paid" | string;

// Define interface for the options object
interface DateTimeFormatOptions {
  timeZone: string;
  year: "numeric";
  month: "2-digit";
  day: "2-digit";
  hour: "2-digit";
  minute: "2-digit";
  second: "2-digit";
  hour12: boolean;
}
export interface OrderData {
  id?: string | number;
  partner_id?: {
    id?: string;
    display_name?: string;
    stud_id?: string;
    tmp_stud_id?: string;
  };
  company?: {
    logo?: string;
    name?: string;
    street?: string;
    street2?: string;
    city?: string;
  };
  tuition_program_id?: {
    display_name?: string;
    name?: string;
    is_surcharge_overdue: boolean;
    res_partner_bank_id: {
      acc_number: string;
    };
    tuition_program_parent_id?: {
      name?: string;
    };
  };

  invoices_payment?: [];
  amount_total?: number | string;
  amount_total_words?: string;
  amount_residual?: number | string;
  invoice_date_due: string;
  state: string;
  payment_state: string;
  is_mix_va: boolean;
  name: string;
  des_payment: string;
  list_balance_applied?: [];
  narration?: any;
  net_total_after_credit?: any;
  net_total_after_credit_word?: any;
  invoice_payment_total_amount?: number | string;
  payment_account_move_transaction_widget: [];
  fundUsage?: any[];
}

interface PrintSettings {
  paperSize: string;
  orientation: "portrait" | "landscape";
  scale: number;
  margins: string;
  colorMode: "color" | "grayscale";
  destination: "printer" | "pdf";
}

// Helper function format currency
const formatCurrencyForPrint = (
  amount: number | string,
  check: boolean
): string => {
  if (!amount || amount === 0) return "0 VNĐ";
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(numAmount)) return "0 VNĐ";
  if (check) {
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(numAmount);
  } else {
    return Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    })
      .format(numAmount)
      .replace(/₫/g, "")
      .trim();
  }
};
const compareDateTime = (
  input: string | Date,
  payment_state: PaymentState,
  state?: any
) => {
  const inputDate = new Date(input);
  const option: Intl.DateTimeFormatOptions = {
    timeZone: "Asia/Bangkok",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  };

  inputDate.setHours(inputDate.getHours() + 14);

  const formattedDate = new Date(inputDate.toLocaleString("en-US", option))
    .toISOString()
    .slice(0, 19)
    .replace("T", " ");

  const currentDate = new Date();
  if (
    new Date(formattedDate) < currentDate &&
    (payment_state === "not_paid" || payment_state === "partial")
  ) {
    return true;
  } else return false;
};

// PrintableInvoice Component
// PrintableInvoice Component - Fixed for single page
const PrintableInvoice: React.FC<{
  orderData: OrderData;
  settings: PrintSettings;
}> = ({ orderData, settings }) => {
  // console.log("orderData", orderData);

  const totalPrice = formatCurrencyForPrint(
    orderData?.net_total_after_credit || 0,
    true
  );
  const remainingAmount = orderData?.amount_residual
    ? formatCurrencyForPrint(orderData.amount_residual, true)
    : null;
  const companyLogo = orderData?.company?.logo || "";
  const companyName = orderData?.company?.name?.toUpperCase() || "";
  const companyAddress = [
    orderData?.company?.street,
    orderData?.company?.street2,
    orderData?.company?.city,
  ]
    .filter(Boolean)
    .join(" ");
  const { t } = useTranslation();
  const invoiceNumber = orderData?.id || "";
  const studentName = orderData?.partner_id?.display_name || "";
  const studentId = orderData?.partner_id?.stud_id || "";
  const tmpStudId = orderData?.partner_id?.tmp_stud_id || "";
  const programName = orderData?.tuition_program_id?.display_name || "";
  const amountWords = orderData?.net_total_after_credit_word || "";
  const userId = localStorage.getItem("userId");
  const shortName = localStorage.getItem("shortName");
  const listTransaction =
    orderData?.payment_account_move_transaction_widget || [];
  const usedCredits = Array.isArray(orderData?.fundUsage)
    ? orderData.fundUsage.filter((item: any) => item.target && item.target !== "-" && item.target !== "Other")
    : [];

  const isAccNumber = orderData?.is_mix_va
    ? orderData?.name
    : orderData?.tuition_program_id?.res_partner_bank_id?.acc_number;

  const isValid =
    orderData?.invoice_date_due && orderData?.state && orderData?.payment_state
      ? orderData?.payment_state === "paid"
        ? false
        : compareDateTime(
          orderData.invoice_date_due,
          orderData.payment_state,
          orderData.state
        )
          ? orderData?.tuition_program_id?.is_surcharge_overdue
            ? true
            : false
          : true
      : true;

  const getPageDimensions = () => {
    const sizes: Record<string, { width: number; height: number }> = {
      A4: { width: 210, height: 297 },
      A3: { width: 297, height: 420 },
      A5: { width: 148, height: 210 },
      Letter: { width: 216, height: 279 },
      Legal: { width: 216, height: 356 },
    };

    const size = sizes[settings.paperSize] || sizes.A4;
    return settings.orientation === "landscape"
      ? { width: size.height, height: size.width }
      : size;
  };

  const dimensions = getPageDimensions();
  const scaleTransform = `scale(${settings.scale / 100})`;

  const containerStyle: React.CSSProperties = {
    width: `${dimensions.width}mm`,
    height: `${dimensions.height}mm`,
    transform: scaleTransform,
    transformOrigin: "top left",
    fontFamily: "Arial, sans-serif",
    fontSize: "11px", // Giảm fontSize để tiết kiệm không gian
    background: "white",
    color: settings.colorMode === "grayscale" ? "#000" : "#0E121B",
    lineHeight: "1.3", // Giảm line-height
    padding:
      settings.margins === "none"
        ? "3mm" // Giảm padding
        : settings.margins === "minimum"
          ? "6mm"
          : "10mm",
    boxSizing: "border-box",
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
  };

  return (
    <div style={containerStyle}>
      <div>
        {/* Header - Giảm kích thước */}
        <div
          style={{
            display: "flex",
            gap: "10px", // Giảm gap
            width: "100%",
            borderBottom: "1px solid rgb(222, 226, 230)",
            paddingBottom: "6px", // Giảm padding
            marginBottom: "8px", // Giảm margin
            minHeight: "40px", // Giảm height
          }}
        >
          <div
            style={{
              maxWidth: "60px", // Giảm kích thước logo
              height: "35px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            {companyLogo && (
              <img
                src={companyLogo}
                alt="logo"
                style={{
                  maxWidth: "60px",
                  maxHeight: "35px",
                  width: "auto",
                  height: "auto",
                  objectFit: "contain",
                  filter:
                    settings.colorMode === "grayscale"
                      ? "grayscale(100%)"
                      : "none",
                }}
              />
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontWeight: "bold",
                fontSize: "12px",
                marginBottom: "3px",
              }}
            >
              {companyName}
            </div>
            <div style={{ fontSize: "10px" }}>{companyAddress}</div>
          </div>
        </div>

        {/* Title - Giảm kích thước */}
        <div style={{ textAlign: "center", margin: "10px 0" }}>
          <div
            style={{
              fontSize: "16px", // Giảm từ 20px
              fontWeight: "bold",
              textTransform: "uppercase",
              marginBottom: "5px",
            }}
          >
            {t("tuttion_fee_colection_bill")}
          </div>
          <div style={{ fontWeight: 600, fontSize: "11px" }}>
            {t("number")}: {invoiceNumber}
          </div>
        </div>

        {/* Details - Giảm kích thước */}
        <div style={{ marginBottom: "8px" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "10px",
            }}
          >
            <tbody>
              <tr>
                <td
                  style={{
                    padding: "2px 0px",
                    fontWeight: "normal",
                    width: "30%",
                  }}
                >
                  {t("firt_lastname_student")}:
                </td>
                <td>{studentName}</td>
              </tr>
              {studentId && (
                <tr>
                  <td
                    style={{
                      padding: "2px 0px",
                      fontWeight: "normal",
                    }}
                  >
                    {t("student_code")}:
                  </td>
                  <td>{studentId}</td>
                </tr>
              )}
              {tmpStudId && !orderData?.partner_id?.stud_id && (
                <tr>
                  <td
                    style={{
                      padding: "2px 0px",
                      fontWeight: "normal",
                    }}
                  >
                    {t("application_id")}:
                  </td>
                  <td>
                    {orderData?.partner_id?.stud_id
                      ? orderData?.partner_id?.stud_id
                      : tmpStudId}
                  </td>
                </tr>
              )}
              <tr>
                <td
                  style={{
                    padding: "2px 0px",
                    fontWeight: "normal",
                  }}
                >
                  {t("content")}:
                </td>
                <td>
                  {/* {t("collect-tuition-fees")} {programName} */}
                  {
                    orderData?.tuition_program_id?.tuition_program_parent_id
                      ?.name
                  }{" "}
                  - {orderData?.tuition_program_id?.name || ""}
                  {orderData?.narration &&
                    " - " + orderData?.narration?.replace(/<[^>]+>/g, "")}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Combined Transaction & Credits Table */}
        {(listTransaction.length > 0 || usedCredits.length > 0) && (
          <div
            style={{
              border: "1px solid rgba(153,160,174,0.5)",
              borderRadius: "4px",
              overflow: "hidden",
              marginBottom: "8px",
            }}
          >
            <table
              style={{
                borderCollapse: "collapse",
                width: "100%",
                fontSize: "9px",
              }}
            >
              {listTransaction.length > 0 && (
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(153,160,174,0.5)" }}>
                    <th
                      style={{
                        padding: "6px 10px",
                        textAlign: "left",
                        color: "#0E121B",
                        fontSize: "9px",
                        fontWeight: "700",
                      }}
                    >
                      {t("tuition_management_hour_date")}
                    </th>
                    <th
                      style={{
                        padding: "6px 10px",
                        textAlign: "left",
                        color: "#0E121B",
                        fontSize: "9px",
                        fontWeight: "700",
                      }}
                    >
                      {t("tuition_management_amount")}
                    </th>
                    <th
                      style={{
                        padding: "6px 10px",
                        textAlign: "left",
                        color: "#0E121B",
                        fontSize: "9px",
                        fontWeight: "700",
                      }}
                    >
                      {t("payment_method")}
                    </th>
                  </tr>
                </thead>
              )}
              <tbody>
                {listTransaction
                  .slice(0, isValid ? 3 : 8) // Giảm số dòng hiển thị
                  .map((row: any, index: number) => (
                    <tr
                      key={index}
                      style={{ borderBottom: "1px solid rgba(153,160,174,0.5)" }}
                    >
                      <td
                        style={{
                          padding: "6px 10px",
                          fontSize: "9px",
                          fontWeight: "500",
                          color: "#0E121B",
                        }}
                      >
                        {moment(row?.trans_time || row?.create_date)
                          .add(7, "hour")
                          .format("DD/MM/YYYY")}
                      </td>
                      <td
                        style={{
                          padding: "6px 10px",
                          fontSize: "9px",
                          fontWeight: "500",
                          color: "#0E121B",
                        }}
                      >
                        {row?.amount
                          ? formatCurrencyForPrint(row?.amount, false) + " VND"
                          : "0 VND"}
                      </td>
                      <td
                        style={{
                          padding: "6px 10px",
                          fontSize: "9px",
                          fontWeight: "500",
                          color: "#0E121B",
                        }}
                      >
                        {row?.move_type === "out_refund"
                          ? t("apply_credit")
                          : row?.journal_name}
                      </td>
                    </tr>
                  ))}

                {/* "Tổng đã đóng" row */}
                <tr
                  style={{
                    borderBottom: usedCredits.length > 0 ? "1px solid rgba(153,160,174,0.5)" : "none",
                    backgroundColor: "#F8F9FA",
                  }}
                >
                  <td
                    style={{
                      padding: "6px 10px",
                      fontSize: "9px",
                      fontWeight: "700",
                      color: "#0E121B",
                    }}
                  >
                    Tổng đã đóng:
                  </td>
                  <td
                    colSpan={2}
                    style={{
                      padding: "6px 10px",
                      fontSize: "9px",
                      fontWeight: "700",
                      color: "#0E121B",
                      textAlign: "right",
                    }}
                  >
                    {formatCurrencyForPrint(orderData?.invoice_payment_total_amount || 0, false)} VND
                  </td>
                </tr>

                {/* "Ghi chú" (usedCredits) row */}
                {usedCredits.length > 0 && (
                  <tr>
                    <td
                      colSpan={3}
                      style={{
                        padding: "6px 10px",
                        fontSize: "9px",
                        color: "#0E121B",
                      }}
                    >
                      <div style={{ display: "flex", gap: "6px" }}>
                        <span style={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                          {t("note")}:
                        </span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                            {t("used_credit_other_receipts_detail")}
                          </div>
                          <ul style={{ listStyleType: "none", paddingLeft: 0, margin: 0 }}>
                            {usedCredits.map((item: any, idx: number) => {
                              const isRefund = item.state === "Refunded";
                              return (
                                <li
                                  key={idx}
                                  style={{
                                    marginBottom: "2px",
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    gap: "24px",
                                  }}
                                >
                                  <div style={{ minWidth: "170px" }}>
                                    • {isRefund ? t("refund_receipt") : t("payment_receipt")}: {item.target}
                                  </div>
                                  <div style={{ fontWeight: "bold" }}>
                                    {item.amount
                                      ? formatCurrencyForPrint(item.amount, false) + " VND"
                                      : "0 VND"}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Amount - Giảm kích thước */}
        <div style={{ marginBottom: "10px" }}>
          <table style={{ width: "100%", fontSize: "11px" }}>
            <tbody>
              <tr>
                <td
                  style={{
                    fontWeight: "normal",
                    width: "30%",
                    paddingBottom: "4px",
                  }}
                >
                  {t("tuition_management_total_amount")}:
                </td>
                <td
                  style={{
                    fontWeight: "bold",
                    color: "#000",
                  }}
                >
                  {totalPrice}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: "normal", paddingBottom: "4px" }}>
                  {t("in_words")}:
                </td>
                <td style={{ fontWeight: "bold" }}>{amountWords}</td>
              </tr>
              {remainingAmount != totalPrice && (
                <tr>
                  <td style={{ fontWeight: "normal" }}>
                    {t("remaining-amount")}:
                  </td>
                  <td style={{ fontWeight: "bold" }}>
                    {remainingAmount ? remainingAmount : 0}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom Section - Flex để điều chỉnh layout */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-around",
            borderTop: "1px solid rgb(222, 226, 230)",
            paddingTop: "8px",
            marginBottom: "8px",
          }}
        >
          <div style={{ textAlign: "center", flex: 1 }}>
            <div
              style={{
                fontWeight: "bold",
                fontSize: "10px",
                marginBottom: "30px", // Giảm margin
              }}
            >
              {t("payer")}
              <div
                style={{
                  fontSize: "7px",
                  fontStyle: "italic",
                  fontWeight: "lighter",
                }}
              >
                ({t("payment_note")})
              </div>
            </div>
          </div>
          <div style={{ textAlign: "center", flex: 1 }}>
            <div
              style={{
                fontWeight: "bold",
                fontSize: "10px",
                marginBottom: "40px",
              }}
            >
              {userId === "ocb_collector" && `OCB - `}
              {t("cashier")}
              <div
                style={{
                  fontSize: "7px",
                  fontStyle: "italic",
                  fontWeight: "lighter",
                }}
              >
                ({t("payment_note")})
              </div>
            </div>
          </div>
          {userId === "ocb_collector" && (
            <div style={{ textAlign: "center", flex: 1 }}>
              <div
                style={{
                  fontWeight: "bold",
                  fontSize: "10px",
                  marginBottom: "40px",
                }}
              >
                {shortName} - {t("collector")}
                <div
                  style={{
                    fontSize: "7px",
                    fontStyle: "italic",
                    fontWeight: "lighter",
                  }}
                >
                  ({t("payment_note")})
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Notes - Giảm kích thước */}
        <div
          style={{
            padding: "6px",
            borderRadius: "3px",
          }}
        >
          <div
            style={{
              fontWeight: "bold",
              fontSize: "10px",
              marginBottom: "3px",
            }}
          >
            {t("employee_note")}:
          </div>
          <ul style={{ paddingLeft: "12px", margin: 0, fontSize: "9px" }}>
            <li style={{ marginBottom: "2px" }}>{t("payment_note1")}</li>
            <li>{t("payment_note2")}</li>
          </ul>
        </div>
        {isValid && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "5px",
              }}
            >
              <div
                style={{
                  width: "120px", // Giảm kích thước QR từ 200px xuống 120px
                  height: "120px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <QRCodeComponent
                  amount={JSON.stringify(orderData?.amount_residual)}
                  description={orderData?.des_payment || ""}
                  accountNumber={isAccNumber}
                />
              </div>
              <div
                style={{
                  fontSize: "9px", // Giảm font size
                  color: "#0E121B",
                  fontWeight: "bold",
                  textAlign: "center",
                  lineHeight: "1.2",
                }}
              >
                {t("sell_management_scan_qr")}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* QR Code - Nếu có */}
    </div>
  );
};

// Print Preview Sidebar Component
const PrintPreviewSidebar: React.FC<{
  settings: PrintSettings;
  onSettingsChange: (settings: PrintSettings) => void;
  onPrint: () => void;
  onClose: () => void;
}> = ({ settings, onSettingsChange, onPrint, onClose }) => {
  const { t } = useTranslation();
  const handleSettingChange = (key: keyof PrintSettings, value: any) => {
    onSettingsChange({ ...settings, [key]: value });
  };

  return (
    <div
      style={{
        width: "300px",
        height: "100vh",
        backgroundColor: "#f8f9fa",
        borderLeft: "1px solid #dee2e6",
        padding: "20px",
        overflow: "auto",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          paddingBottom: "15px",
          borderBottom: "1px solid #dee2e6",
        }}
      >
        <h3 style={{ margin: 0, fontSize: "18px" }}>{t("print_setting")}</h3>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            fontSize: "20px",
            cursor: "pointer",
            padding: "5px",
          }}
        >
          ×
        </button>
      </div>

      {/* Destination */}
      <div style={{ marginBottom: "20px" }}>
        <label
          style={{ display: "block", fontWeight: "bold", marginBottom: "8px" }}
        >
          {t("destination")}
        </label>
        <select
          value={settings.destination}
          onChange={(e) => handleSettingChange("destination", e.target.value)}
          style={{
            width: "100%",
            padding: "8px",
            border: "1px solid #ced4da",
            borderRadius: "4px",
            fontSize: "14px",
          }}
        >
          <option value="printer">🖨️ {t("report_table_print")}</option>
          <option value="pdf">📄 Save as PDF</option>
        </select>
      </div>

      {/* Paper Size */}
      <div style={{ marginBottom: "20px" }}>
        <label
          style={{ display: "block", fontWeight: "bold", marginBottom: "8px" }}
        >
          {t("page_size")}
        </label>
        <select
          value={settings.paperSize}
          onChange={(e) => handleSettingChange("paperSize", e.target.value)}
          style={{
            width: "100%",
            padding: "8px",
            border: "1px solid #ced4da",
            borderRadius: "4px",
            fontSize: "14px",
          }}
        >
          <option value="A4">A4</option>
          <option value="A3">A3</option>
          <option value="A5">A5</option>
          <option value="Letter">Letter</option>
          <option value="Legal">Legal</option>
        </select>
      </div>

      {/* Orientation */}
      <div style={{ marginBottom: "20px" }}>
        <label
          style={{ display: "block", fontWeight: "bold", marginBottom: "8px" }}
        >
          {t("layout")}
        </label>
        <div style={{ display: "flex", gap: "10px" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              cursor: "pointer",
            }}
          >
            <input
              type="radio"
              value="portrait"
              checked={settings.orientation === "portrait"}
              onChange={(e) =>
                handleSettingChange("orientation", e.target.value)
              }
            />
            Portrait
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              cursor: "pointer",
            }}
          >
            <input
              type="radio"
              value="landscape"
              checked={settings.orientation === "landscape"}
              onChange={(e) =>
                handleSettingChange("orientation", e.target.value)
              }
            />
            Landscape
          </label>
        </div>
      </div>

      {/* Scale */}
      <div style={{ marginBottom: "20px" }}>
        <label
          style={{ display: "block", fontWeight: "bold", marginBottom: "8px" }}
        >
          Scale: {settings.scale}%
        </label>
        <input
          type="range"
          min="25"
          max="200"
          step="5"
          value={settings.scale}
          onChange={(e) =>
            handleSettingChange("scale", parseInt(e.target.value))
          }
          style={{
            width: "100%",
            marginBottom: "8px",
          }}
        />
        <div style={{ display: "flex", gap: "5px" }}>
          <button
            onClick={() => handleSettingChange("scale", 50)}
            style={{
              padding: "4px 8px",
              border: "1px solid #ced4da",
              background: settings.scale === 50 ? "#575757" : "white",
              color: settings.scale === 50 ? "white" : "black",
              borderRadius: "3px",
              cursor: "pointer",
              fontSize: "12px",
            }}
          >
            50%
          </button>
          <button
            onClick={() => handleSettingChange("scale", 100)}
            style={{
              padding: "4px 8px",
              border: "1px solid #ced4da",
              background: settings.scale === 100 ? "#575757" : "white",
              color: settings.scale === 100 ? "white" : "black",
              borderRadius: "3px",
              cursor: "pointer",
              fontSize: "12px",
            }}
          >
            100%
          </button>
          <button
            onClick={() => handleSettingChange("scale", 150)}
            style={{
              padding: "4px 8px",
              border: "1px solid #ced4da",
              background: settings.scale === 150 ? "#575757" : "white",
              color: settings.scale === 150 ? "white" : "black",
              borderRadius: "3px",
              cursor: "pointer",
              fontSize: "12px",
            }}
          >
            150%
          </button>
        </div>
      </div>

      {/* Margins */}
      <div style={{ marginBottom: "20px" }}>
        <label
          style={{ display: "block", fontWeight: "bold", marginBottom: "8px" }}
        >
          Margins
        </label>
        <select
          value={settings.margins}
          onChange={(e) => handleSettingChange("margins", e.target.value)}
          style={{
            width: "100%",
            padding: "8px",
            border: "1px solid #ced4da",
            borderRadius: "4px",
            fontSize: "14px",
          }}
        >
          <option value="default">Default</option>
          <option value="none">None</option>
          <option value="minimum">Minimum</option>
        </select>
      </div>

      {/* Color Mode */}
      {/* <div style={{ marginBottom: "30px" }}>
        <label
          style={{ display: "block", fontWeight: "bold", marginBottom: "8px" }}
        >
          Options
        </label>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={settings.colorMode === "grayscale"}
            onChange={(e) =>
              handleSettingChange(
                "colorMode",
                e.target.checked ? "grayscale" : "color"
              )
            }
          />
          Print in grayscale
        </label>
      </div> */}

      {/* Print Buttons */}
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <button
          onClick={onPrint}
          style={{
            padding: "12px 20px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {settings.destination === "pdf"
            ? "📄 Save as PDF"
            : `🖨️ ${t("report_table_print")}`}
        </button>
        <button
          onClick={onClose}
          style={{
            padding: "12px 20px",
            backgroundColor: "#6c757d",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            fontSize: "16px",
          }}
        >
          {t("cancel")}
        </button>
      </div>
    </div>
  );
};

// Main Print Preview Component
const CustomPrintPreview: React.FC<{
  orderData: OrderData;
  onClose: () => void;
}> = ({ orderData, onClose }) => {
  const [settings, setSettings] = useState<PrintSettings>({
    paperSize: "A5",
    orientation: "portrait",
    scale: 100,
    margins: "default",
    colorMode: "color",
    destination: "printer",
  });

  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Biên lai thu tiền - ${orderData?.partner_id?.id || "N/A"}`,
    onAfterPrint: () => { },
    onPrintError: (errorLocation, error) => {
      console.error("Lỗi khi in:", errorLocation, error);
    },
  });

  const handlePrintAction = () => {
    if (settings.destination === "pdf") {
      // Trigger browser's save as PDF
      handlePrint();
    } else {
      // Trigger normal print
      handlePrint();
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0,0,0,0.8)",
        zIndex: 10000,
        display: "flex",
      }}
    >
      {/* Preview Area */}
      <div
        style={{
          flex: 1,
          backgroundColor: "#e5e5e5",
          padding: "20px",
          overflow: "auto",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        <div
          style={{
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
            backgroundColor: "white",
            margin: "20px",
          }}
        >
          <div ref={printRef}>
            <PrintableInvoice orderData={orderData} settings={settings} />
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <PrintPreviewSidebar
        settings={settings}
        onSettingsChange={setSettings}
        onPrint={handlePrintAction}
        onClose={onClose}
      />
    </div>
  );
};

// Export function to use in ButtonView
export const printSafely = (orderData: OrderData): void => {
  const tempContainer = document.createElement("div");
  document.body.appendChild(tempContainer);

  const root = createRoot(tempContainer);

  const handleClose = () => {
    root.unmount();
    if (document.body.contains(tempContainer)) {
      document.body.removeChild(tempContainer);
    }
  };

  root.render(
    <CustomPrintPreview orderData={orderData} onClose={handleClose} />
  );
};
